import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROLE, roleLabel, type Role } from '@ovrsee/shared'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { FormModal } from '../../components/FormModal.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Button, Chip, Input, Select, Text } from '../../ui/index.js'

interface PersonRow {
  readonly id: string
  readonly name: string
  readonly email: string
  readonly role: Role
  readonly jobTitle: string | null
  readonly departmentId: string | null
  readonly managerId: string | null
  readonly isActive: boolean
}

interface DepartmentRow {
  readonly id: string
  readonly name: string
}

interface InviteRow {
  readonly id: string
  readonly email: string
  readonly role: Role
  readonly jobTitle: string | null
  readonly departmentId: string | null
  readonly managerId: string | null
  readonly expiresAt: string
  readonly createdAt: string
}

type ActiveValue = 'true' | 'false'

function departmentName(person: PersonRow, departments: readonly DepartmentRow[]): string {
  return departments.find((department) => department.id === person.departmentId)?.name ?? 'Unassigned'
}

function managerName(person: PersonRow, people: readonly PersonRow[]): string {
  return people.find((candidate) => candidate.id === person.managerId)?.name ?? '—'
}

function roleChoices(): readonly Role[] {
  return [ROLE.SUPER_MANAGER, ROLE.MANAGER, ROLE.EMPLOYEE]
}

function buildPatch(person: PersonRow, draft: {
  departmentId: string
  managerId: string
  jobTitle: string
  role: Role
  isActive: ActiveValue
}): Record<string, unknown> {
  const patch: Record<string, unknown> = {}

  const nextDepartmentId = draft.departmentId === '' ? null : draft.departmentId
  if (nextDepartmentId !== person.departmentId) patch['departmentId'] = nextDepartmentId

  const nextManagerId = draft.managerId === '' ? null : draft.managerId
  if (nextManagerId !== person.managerId) patch['managerId'] = nextManagerId

  const nextJobTitle = draft.jobTitle.trim() === '' ? null : draft.jobTitle.trim()
  if (nextJobTitle !== (person.jobTitle ?? null)) patch['jobTitle'] = nextJobTitle

  if (draft.role !== person.role) patch['role'] = draft.role

  const nextIsActive = draft.isActive === 'true'
  if (nextIsActive !== person.isActive) patch['isActive'] = nextIsActive

  return patch
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso))
}

function PeopleTable({
  people,
  departments,
  onEdit,
}: {
  readonly people: readonly PersonRow[]
  readonly departments: readonly DepartmentRow[]
  readonly onEdit: (person: PersonRow) => void
}) {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        overflowX: 'auto', overflowY: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Name</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Email</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Role</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Department</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Manager</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Job title</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Status</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {people.map((person) => {
            return (
              <tr key={person.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)', fontWeight: 500 }}>{person.name}</td>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{person.email}</td>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{roleLabel(person.role)}</td>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{departmentName(person, departments)}</td>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{managerName(person, people)}</td>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{person.jobTitle ?? '—'}</td>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>
                  <Chip label={person.isActive ? 'Active' : 'Inactive'} color={person.isActive ? 'positive' : 'negative'} />
                </td>
                <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>
                  <Button size="small" onClick={() => onEdit(person)}>
                    Edit
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}

function buildInviteBody(draft: {
  email: string
  role: Role
  departmentId: string
  jobTitle: string
}): Record<string, unknown> {
  const body: Record<string, unknown> = {
    email: draft.email.trim(),
    role: draft.role,
  }
  if (draft.departmentId !== '') body['departmentId'] = draft.departmentId
  if (draft.jobTitle.trim() !== '') body['jobTitle'] = draft.jobTitle.trim()
  return body
}

function InvitePersonModal({
  isOpen,
  onClose,
  onSubmit,
  departments,
  busy,
}: {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onSubmit: (body: Record<string, unknown>) => Promise<void>
  readonly departments: readonly DepartmentRow[]
  readonly busy: boolean
}) {
  const [email, setEmail] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [role, setRole] = useState<Role>(ROLE.EMPLOYEE)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setEmail('')
    setDepartmentId('')
    setJobTitle('')
    setRole(ROLE.EMPLOYEE)
    setFormError(null)
  }, [isOpen])

  const canSubmit = email.trim() !== ''

  return (
    <FormModal
      title="Invite person"
      isOpen={isOpen}
      onClose={() => {
        setFormError(null)
        onClose()
      }}
      onSubmit={() => {
        setFormError(null)
        void onSubmit(buildInviteBody({ email, role, departmentId, jobTitle })).catch((error) => {
          setFormError(error instanceof ApiError ? error.message : 'Could not invite that person.')
        })
      }}
      submitLabel="Invite"
      canSubmit={canSubmit}
      busy={busy}
    >
      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="invite-email">
          Email
        </label>
        <Input id="invite-email" aria-label="Email" value={email} onChange={setEmail} />
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="invite-role">
          Role
        </label>
        <Select id="invite-role" aria-label="Role" value={role} onChange={(value) => setRole(value as Role)}>
          {roleChoices().map((option) => (
            <option key={option} value={option}>
              {roleLabel(option)}
            </option>
          ))}
        </Select>
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="invite-department">
          Department
        </label>
        <Select id="invite-department" aria-label="Department" value={departmentId} onChange={setDepartmentId}>
          <option value="">No department</option>
          {departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="invite-job-title">
          Job title
        </label>
        <Input id="invite-job-title" aria-label="Job title" value={jobTitle} onChange={setJobTitle} />
      </div>

      {formError !== null ? <Text color="secondary">{formError}</Text> : null}
    </FormModal>
  )
}

function PendingInvites({
  invites,
  onRevoke,
  busy,
}: {
  readonly invites: readonly InviteRow[]
  readonly onRevoke: (id: string) => Promise<void>
  readonly busy: boolean
}) {
  if (invites.length === 0) return null

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        overflowX: 'auto', overflowY: 'hidden',
      }}
    >
      <div style={{ padding: 'var(--o-space-4) var(--o-space-4) 0' }}>
        <Text style={{ fontWeight: 600 }}>Pending invites</Text>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 'var(--o-space-3)' }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Email</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Role</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Expires</th>
            <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((invite) => (
            <tr key={invite.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{invite.email}</td>
              <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{roleLabel(invite.role)}</td>
              <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{formatDate(invite.expiresAt)}</td>
              <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    void onRevoke(invite.id)
                  }}
                >
                  Revoke
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

export function PeopleTab() {
  const { api, user } = useAuth()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<PersonRow | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<PersonRow[]>('/api/users'),
  })

  const departmentsQuery = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: () => api.get<DepartmentRow[]>('/api/departments'),
  })

  const invitesQuery = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => api.get<InviteRow[]>('/api/invites'),
  })

  const updatePerson = useMutation({
    mutationFn: (input: { id: string; patch: Record<string, unknown> }) =>
      api.patch<PersonRow>(`/api/users/${input.id}`, input.patch),
    onSuccess: async () => {
      setEditing(null)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  const createInvite = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<InviteRow>('/api/invites', body),
    onSuccess: async () => {
      setInviteOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] })
    },
  })

  const revokeInvite = useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/invites/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] })
    },
  })

  const loading = usersQuery.isPending || departmentsQuery.isPending || invitesQuery.isPending
  if (loading) return <LoadingState label="Loading people" />

  if (usersQuery.isError || departmentsQuery.isError || invitesQuery.isError) {
    return (
      <ErrorState
        error={usersQuery.error ?? departmentsQuery.error ?? invitesQuery.error}
        onRetry={() =>
          void Promise.all([usersQuery.refetch(), departmentsQuery.refetch(), invitesQuery.refetch()])
        }
      />
    )
  }

  const people = usersQuery.data ?? []
  const departments = departmentsQuery.data ?? []
  const invites = invitesQuery.data ?? []

  return (
    <div style={{ display: 'grid', gap: 'var(--o-space-5)' }}>
      <div className="row-actions" style={{ justifyContent: 'flex-start' }}>
        <Button kind="primary" onClick={() => setInviteOpen(true)}>
          Invite person
        </Button>
      </div>

      <PeopleTable
        people={people}
        departments={departments}
        onEdit={(person) => setEditing(person)}
      />

      <PendingInvites
        invites={invites}
        busy={revokeInvite.isPending}
        onRevoke={async (id) => {
          await revokeInvite.mutateAsync(id)
        }}
      />

      <InvitePersonModal
        isOpen={inviteOpen}
        busy={createInvite.isPending}
        departments={departments}
        onClose={() => setInviteOpen(false)}
        onSubmit={async (body) => {
          await createInvite.mutateAsync(body)
        }}
      />

      <EditPersonModal
        key={editing?.id ?? 'none'}
        person={editing}
        people={people}
        departments={departments}
        currentUserId={user?.id ?? ''}
        isOpen={editing !== null}
        busy={updatePerson.isPending}
        onClose={() => setEditing(null)}
        onSubmit={async (patch) => {
          if (editing === null) return
          await updatePerson.mutateAsync({ id: editing.id, patch })
        }}
      />
    </div>
  )
}

function EditPersonModal({
  person,
  people,
  departments,
  currentUserId,
  isOpen,
  onClose,
  onSubmit,
  busy,
}: {
  readonly person: PersonRow | null
  readonly people: readonly PersonRow[]
  readonly departments: readonly DepartmentRow[]
  readonly currentUserId: string
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onSubmit: (patch: Record<string, unknown>) => Promise<void>
  readonly busy: boolean
}) {
  const [departmentId, setDepartmentId] = useState(person?.departmentId ?? '')
  const [managerId, setManagerId] = useState(person?.managerId ?? '')
  const [jobTitle, setJobTitle] = useState(person?.jobTitle ?? '')
  const [role, setRole] = useState<Role>(person?.role ?? ROLE.EMPLOYEE)
  const [isActive, setIsActive] = useState<ActiveValue>(person?.isActive ? 'true' : 'false')
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || person === null) return
    setDepartmentId(person.departmentId ?? '')
    setManagerId(person.managerId ?? '')
    setJobTitle(person.jobTitle ?? '')
    setRole(person.role)
    setIsActive(person.isActive ? 'true' : 'false')
    setFormError(null)
  }, [isOpen, person])

  if (person === null) return null

  const managerChoices = people.filter(
    (candidate) =>
      candidate.id !== person.id &&
      (candidate.role === ROLE.MANAGER || candidate.role === ROLE.SUPER_MANAGER),
  )
  const roleLocked = currentUserId === person.id
  const activeSuperCount = people.filter(
    (candidate) => candidate.role === ROLE.SUPER_MANAGER && candidate.isActive,
  ).length
  const activeLocked = person.role === ROLE.SUPER_MANAGER && person.isActive && activeSuperCount === 1

  const patch = buildPatch(person, { departmentId, managerId, jobTitle, role, isActive })
  const canSubmit = Object.keys(patch).length > 0

  return (
    <FormModal
      title={`Edit ${person.name}`}
      isOpen={isOpen}
      onClose={() => {
        setFormError(null)
        onClose()
      }}
      onSubmit={() => {
        setFormError(null)
        void onSubmit(patch).catch((error) => {
          setFormError(error instanceof ApiError ? error.message : 'Could not update that person.')
        })
      }}
      submitLabel="Save changes"
      canSubmit={canSubmit}
      busy={busy}
    >
      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="person-department">
          Department
        </label>
        <Select id="person-department" aria-label="Department" value={departmentId} onChange={(value) => setDepartmentId(value)}>
          <option value="">No department</option>
          {departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="person-manager">
          Manager
        </label>
        <Select id="person-manager" aria-label="Manager" value={managerId} onChange={(value) => setManagerId(value)}>
          <option value="">No manager</option>
          {managerChoices.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="person-job-title">
          Job title
        </label>
        <Input id="person-job-title" aria-label="Job title" value={jobTitle} onChange={(value) => setJobTitle(value)} />
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="person-role">
          Role
        </label>
        <Select
          id="person-role"
          aria-label="Role"
          value={role}
          onChange={(value) => setRole(value as Role)}
          disabled={roleLocked}
        >
          {roleChoices().map((option) => (
            <option key={option} value={option}>
              {roleLabel(option)}
            </option>
          ))}
        </Select>
        {roleLocked ? <Text color="secondary">You cannot change your own role.</Text> : null}
      </div>

      <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
        <label className="field-label" htmlFor="person-active">
          Active
        </label>
        <Select
          id="person-active"
          aria-label="Active"
          value={isActive}
          onChange={(value) => setIsActive(value as ActiveValue)}
          disabled={activeLocked}
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </Select>
        {activeLocked ? <Text color="secondary">The last active super manager must stay active.</Text> : null}
      </div>

      {formError !== null ? <Text color="secondary">{formError}</Text> : null}
    </FormModal>
  )
}
